import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLauncherContext } from "../../../../lib/client/contexts/LauncherContext";

type BlobState = {
  url?: string;
  error?: string;
};

type BlobsContextType = {
  fetchBlob: (conversationId: string, fileUid: string) => BlobState | null;
};

const BlobsContext = createContext<BlobsContextType>({
  fetchBlob: () => null,
});

export const useBlobsContext = () => useContext(BlobsContext);

export const BlobsProvider = ({ children }: { children: ReactNode }) => {
  const [blobs, setBlobs] = useState<{ [blob: string]: BlobState }>({});
  const blobsRequested = useRef<Set<string>>(new Set());
  const { api } = useLauncherContext();

  const downloadBlob = useCallback(
    (conversationId: string, blobId: string) => {
      console.debug(`Downloading blob ${blobId}`);
      api
        .getBlob(conversationId, blobId)
        .then(async (blob) => {
          setBlobs((blobs) => ({
            ...blobs,
            [blobId]: { url: URL.createObjectURL(blob) },
          }));
        })
        .catch((e) => {
          setBlobs((blobs) => ({
            ...blobs,
            [blobId]: { error: e.message },
          }));
        });
    },
    [api],
  );

  const fetchBlob = useCallback(
    (conversationId: string, blobId: string) => {
      const blob = blobs[blobId];
      if (blob) {
        return blob;
      }
      const blobRequested = blobsRequested.current.has(blobId);
      if (!blobRequested) {
        blobsRequested.current.add(blobId);
        downloadBlob(conversationId, blobId);
      }
      return null;
    },
    [blobs, downloadBlob],
  );

  const blob = useMemo(() => ({ fetchBlob }), [fetchBlob]);
  return <BlobsContext.Provider value={blob}>{children}</BlobsContext.Provider>;
};
